import { US_VENUE_COSTS, MEXICO_VENUE_COSTS } from '../data/venueData';

const US_COST_RANGES = {
  venue: { low: 5000, mid: 12000, high: 20000, luxury: 30000 },
  catering: { low: 40, mid: 75, high: 125, luxury: 200 },
  photographer: { low: 1500, mid: 3000, high: 5000, luxury: 8000 },
  dj: { low: 800, mid: 2000, high: 4000, luxury: 7000 },
  flowers: { low: 1000, mid: 3000, high: 6000, luxury: 10000 },
  planner: { low: 0.07, mid: 0.10, high: 0.15, luxury: 0.20 },
  transport: { low: 500, mid: 1200, high: 2500, luxury: 5000 }
};

const MEXICO_COST_RANGES = {
  venue: { low: 3000, mid: 7000, high: 12000, luxury: 20000 },
  catering: { low: 25, mid: 55, high: 90, luxury: 150 },
  photographer: { low: 800, mid: 2000, high: 3500, luxury: 6000 },
  dj: { low: 600, mid: 1500, high: 2500, luxury: 4000 },
  flowers: { low: 800, mid: 2000, high: 4000, luxury: 7000 },
  planner: { low: 0.07, mid: 0.10, high: 0.15, luxury: 0.20 },
  transport: { low: 300, mid: 800, high: 1500, luxury: 3000 }
};

function interpolateCost(budgetLevel, ranges, category) {
  // For planner, ensure minimum value is never 0
  if (category === 'planner') {
    const rangeIndex = Math.min(3, Math.floor(budgetLevel / 25));
    const nextRangeIndex = Math.min(3, rangeIndex + 1);
    const currentValue = Object.values(ranges)[rangeIndex];
    const nextValue = Object.values(ranges)[nextRangeIndex];
    const position = (budgetLevel % 25) / 25;
    return Math.max(0.07, currentValue + (nextValue - currentValue) * position);
  }

  // For other categories, use normal interpolation
  const rangeIndex = Math.min(3, Math.floor(budgetLevel / 25));
  const nextRangeIndex = Math.min(3, rangeIndex + 1);
  const currentValue = Object.values(ranges)[rangeIndex];
  const nextValue = Object.values(ranges)[nextRangeIndex];
  const position = (budgetLevel % 25) / 25;
  return Math.round(currentValue + (nextValue - currentValue) * position);
}

export function calculateBudget(args) {
  try {
    if (!args || typeof args !== 'object') {
      console.error('calculateBudget called with invalid args:', args);
      return { error: 'Invalid arguments passed to calculateBudget.' };
    }
    const { guestCount, style, categories, categoryBudgets } = args;
    console.log('calculateBudget called with:', { guestCount, style, categories, categoryBudgets });
    
    // Validate inputs
    if (!guestCount || !style || !categories || !Array.isArray(categories)) {
      console.error('Invalid input:', { guestCount, style, categories });
      return { error: 'Invalid input parameters' };
    }
    if (typeof style !== 'string' || !style.trim() || style === 'undefined') {
      console.error('Style is undefined or not a string:', style);
      return { error: 'Venue style is missing or invalid.' };
    }

    // Validate venue style exists
    if (!US_VENUE_COSTS[style] || !MEXICO_VENUE_COSTS[style]) {
      console.error('Invalid venue style:', style);
      return { error: `Invalid venue style: ${style}` };
    }

    // Calculate initial costs (excluding planner)
    const usCosts = {};
    const mexicoCosts = {};
    
    categories.forEach(category => {
      if (category === 'planner') return; // Skip planner for now
      
      if (category === 'catering') {
        usCosts[category] = interpolateCost(categoryBudgets?.[category] || 50, US_COST_RANGES.catering, category) * guestCount;
        mexicoCosts[category] = interpolateCost(categoryBudgets?.[category] || 50, MEXICO_COST_RANGES.catering, category) * guestCount;
      } else {
        usCosts[category] = interpolateCost(categoryBudgets?.[category] || 50, US_COST_RANGES[category], category);
        mexicoCosts[category] = interpolateCost(categoryBudgets?.[category] || 50, MEXICO_COST_RANGES[category], category);
      }
    });

    // Calculate initial totals
    const initialUsTotal = Object.values(usCosts).reduce((sum, cost) => sum + cost, 0);
    const initialMexicoTotal = Object.values(mexicoCosts).reduce((sum, cost) => sum + cost, 0);

    // Calculate planner costs as percentage of initial totals
    if (categories.includes('planner')) {
      const usPlannerPercentage = interpolateCost(categoryBudgets?.planner || 50, US_COST_RANGES.planner, 'planner');
      const mexicoPlannerPercentage = interpolateCost(categoryBudgets?.planner || 50, MEXICO_COST_RANGES.planner, 'planner');
      
      usCosts.planner = Math.round(initialUsTotal * usPlannerPercentage);
      mexicoCosts.planner = Math.round(initialMexicoTotal * mexicoPlannerPercentage);
    }

    // Calculate final totals including planner
    const usTotal = Object.values(usCosts).reduce((sum, cost) => sum + cost, 0);
    const mexicoTotal = Object.values(mexicoCosts).reduce((sum, cost) => sum + cost, 0);
    const savings = usTotal - mexicoTotal;

    // Create breakdown for selected categories
    const breakdown = categories.map(category => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      us: Math.round(usCosts[category]),
      mexico: Math.round(mexicoCosts[category])
    }));

    const result = {
      usTotal: Math.round(usTotal),
      mexicoTotal: Math.round(mexicoTotal),
      savings: Math.round(savings),
      breakdown
    };

    return result;
  } catch (error) {
    console.error('Error calculating budget:', error);
    return { error: error.message || 'Unknown error' };
  }
} 